import * as analytic from '@anm/analytic';
import HorizontalTabs from '@anm/components/HorizontalTabs';
import InviteGuestsModal from '@anm/components/stream/InviteGuestsModal';
import useDestinationsList from '@anm/components/stream/destination_management/hooks/useDestinationList';
import useScrolledOut from '@anm/hooks/useScrolledOut';
import { useScrollDirection } from '@anm/hooks/useScrollingUp';
import useSwitchState from '@anm/hooks/useSwitchState';
import { Features } from '@anm/shared/features';
import { FC, useState } from 'react';
import { useSelector } from 'react-redux';

import useFetchUserPrefs from '../../../hooks/useFetchUserPrefs';
import OpenTwoOptionsDialog from '../../OpenTwoOptionsDialog';
import StreamModal from '../../stream/StreamModalWrapper';
import ProjectFolderTreeModalProvider from '../ProjectPlayerBlock/ProjectFolderTreeModalProvider';
import useFetchFolder from '../VideoFolders/hooks/useFetchFolder';
import { userSelectors } from '../../../store/modules/user';

import DestinationsTab from './DestinationsTab';
import InProgressTab from './InProgressTab';
import PastBroadcastTab from './PastTab';
import RightButtons from './RightButtons';
import SuccessStreamModal from './SuccessStreamModal';
import UpcomingTab from './UpcomingTab';
import { Wrapper } from './Wrapper';
import { getStreamParams } from './helpers';
import useCanShowStreamModal from './hooks/useCanShowStreamModal';
import useFetchStreams from './hooks/useFetchStreams';
import useMyStreamTabs from './hooks/useMyStreamsTabs';
import useSuccessModal from './hooks/useSuccessModal';

export type LiveTab = 'upcoming' | 'past' | 'in-progress' | 'destinations';

type MyStreamsSectionProps = {
  currentTab?: LiveTab;
};

const MyStreamsSection: FC<MyStreamsSectionProps> = ({ currentTab }) => {
  const [isNewStreamModalOpened, openNewStreamModal, closeNewStreamModal] = useCanShowStreamModal();
  const [defaultTab, sectionTabs, handleTabChange] = useMyStreamTabs(currentTab);
  const isScrolled = useScrolledOut(30);
  const isMaxSubscription = useSelector(userSelectors.selectIsMaxWavePlan);

  useFetchStreams(getStreamParams(currentTab), currentTab);
  useFetchUserPrefs();

  const [isWebinarSetup, setIsWebinarSetup] = useState(false);

  const handleOpenNewStreamModal = (isWebinar: boolean) => {
    setIsWebinarSetup(isWebinar)
    openNewStreamModal();
    analytic.trackNewStreamOpenDialog();
  };
  const scrollDirection = useScrollDirection();

  const [isSuccessModalOpen, roomUrl, maxGuests, handelCloseSuccessModal] = useSuccessModal();
  const [isInviteModalOpen, openInviteModal, closeInviteModal] = useSwitchState();

  const closeAll = () => {
    closeInviteModal();
    handelCloseSuccessModal();
  };

  const onInviteClick = () => {
    openInviteModal();
    handelCloseSuccessModal();
  };

  return (
    <Wrapper {...{ isScrolled, scrollDirection }} paddingBottom={currentTab === 'past' ? 250 : 100}>
      {Features.isTwoOptionsPopupEnabled && <OpenTwoOptionsDialog />}
      {isSuccessModalOpen && <SuccessStreamModal onInvite={onInviteClick} studioLink={roomUrl} onClose={closeAll} />}
      {isInviteModalOpen && <InviteGuestsModal {...{ maxGuests, isMaxSubscription }} link={roomUrl} onClose={closeAll} />}
      {isNewStreamModalOpened && <StreamModal isSetupStream isWebinarSetup={isWebinarSetup} onClose={closeNewStreamModal} />}
      <HorizontalTabs
        {...{ defaultTab }}
        tabs={sectionTabs}
        onTabChange={handleTabChange}
        renderTabsPanelInnerContent={() => (
          <RightButtons currentTab={defaultTab} onOpenNewStreamModal={handleOpenNewStreamModal} />
        )}
      >
        <UpcomingTab />
        <InProgressTab />
        <PastBroadcastTab />
        <DestinationsTab />
      </HorizontalTabs>
    </Wrapper>
  );
};

const SectionWrapper: FC<MyStreamsSectionProps> = props => {
  useFetchFolder();
  useDestinationsList(props.currentTab !== 'destinations');

  return (
    <ProjectFolderTreeModalProvider>
      <MyStreamsSection {...props} />
    </ProjectFolderTreeModalProvider>
  );
};

export default SectionWrapper;
